// Admin → Settings → Platform Ideas. Manages submissions from the floating
// IDEAS widget shown across the app. Mirrors the mymcflex Flex Ideas admin
// page (status workflow, status filter chips, status badge, screenshot
// thumbnail lightbox, admin/dev notes, widget on/off toggle) but in the
// EVRST design language (gold accent, `card` / `input` / `btn-gold` etc).
import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../../lib/api'

interface Idea {
  id: string
  note: string | null
  adminNotes: string | null
  screenshotName: string | null
  pageUrl: string | null
  userEmail: string | null
  userName: string | null
  userRole: string
  status: 'new' | 'in_review' | 'approved' | 'in_progress' | 'completed'
  createdAt: string
  updatedAt: string
}

const STATUS: Array<[Idea['status'], string]> = [
  ['new',         'New'],
  ['in_review',   'In Review'],
  ['approved',    'Approved'],
  ['in_progress', 'In Progress'],
  ['completed',   'Completed'],
]
const STATUS_LABEL = Object.fromEntries(STATUS) as Record<Idea['status'], string>
// Inline status colours so we don't need to add new utility classes to index.css.
const STATUS_STYLE: Record<Idea['status'], string> = {
  new:         'bg-gold/15 text-gold-dark border border-gold/30',
  in_review:   'bg-amber-50 text-amber-700 border border-amber-200',
  approved:    'bg-blue-50 text-blue-700 border border-blue-200',
  in_progress: 'bg-violet-50 text-violet-700 border border-violet-200',
  completed:   'bg-emerald-50 text-emerald-700 border border-emerald-200',
}
const fmt = (iso: string) => iso
  ? new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })
  : ''

export default function PlatformIdeas(): JSX.Element {
  const { getAccessToken } = useAuth()
  const [items, setItems] = useState<Idea[]>([])
  const [enabled, setEnabled] = useState(true)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | Idea['status']>('all')
  const [lightbox, setLightbox] = useState<string | null>(null)

  async function load() {
    setErr(null)
    try {
      const r = await apiFetch<{ data: Idea[]; enabled: boolean }>(getAccessToken, '/api/admin/ideas')
      setItems(r?.data ?? [])
      setEnabled(r?.enabled !== false)
    } catch (e: unknown) {
      setErr((e as Error)?.message ?? 'Failed to load')
    } finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])  // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: items.length }
    for (const [k] of STATUS) c[k] = 0
    for (const i of items) if (c[i.status] != null) c[i.status]++
    return c
  }, [items])
  const filtered = filter === 'all' ? items : items.filter((i) => i.status === filter)

  async function toggle() {
    const next = !enabled
    setEnabled(next)
    try {
      await apiFetch(getAccessToken, '/api/admin/ideas/config', {
        method: 'PATCH', body: JSON.stringify({ enabled: next }),
      })
    } catch {
      setEnabled(!next)
      alert('Could not update the toggle.')
    }
  }
  async function setStatus(id: string, status: Idea['status']) {
    setItems((p) => p.map((i) => (i.id === id ? { ...i, status } : i)))
    try {
      await apiFetch(getAccessToken, `/api/admin/ideas/${id}`, {
        method: 'PATCH', body: JSON.stringify({ status }),
      })
    } catch { load() }
  }
  async function saveNote(id: string, note: string) {
    try {
      await apiFetch(getAccessToken, `/api/admin/ideas/${id}`, {
        method: 'PATCH', body: JSON.stringify({ note }),
      })
      load()
    } catch (e: unknown) { alert((e as Error)?.message ?? 'Could not save') }
  }
  async function saveAdminNotes(id: string, adminNotes: string) {
    setItems((p) => p.map((i) => (i.id === id ? { ...i, adminNotes } : i)))
    try {
      await apiFetch(getAccessToken, `/api/admin/ideas/${id}`, {
        method: 'PATCH', body: JSON.stringify({ adminNotes }),
      })
    } catch (e: unknown) {
      alert((e as Error)?.message ?? 'Could not save'); load()
    }
  }
  async function remove(id: string) {
    if (!confirm('Delete this submission?')) return
    try {
      await apiFetch(getAccessToken, `/api/admin/ideas/${id}`, { method: 'DELETE' })
      setItems((p) => p.filter((i) => i.id !== id))
    } catch (e: unknown) { alert((e as Error)?.message ?? 'Delete failed') }
  }

  if (loading) return <div className="p-8 text-ink-3 text-sm">Loading…</div>

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink leading-tight">Platform Ideas</h1>
          <p className="text-ink-3 text-sm mt-1">Feedback and ideas submitted from the floating IDEAS widget across the app.</p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <span className="text-ink-2 font-medium">Widget {enabled ? 'on' : 'off'}</span>
          <button
            onClick={toggle}
            role="switch"
            aria-checked={enabled}
            className={'relative inline-flex h-6 w-11 items-center rounded-full transition-colors ' + (enabled ? 'bg-gold' : 'bg-border')}
          >
            <span className={'inline-block h-5 w-5 transform rounded-full bg-white transition-transform ' + (enabled ? 'translate-x-5' : 'translate-x-0.5')} />
          </button>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4 mb-4">
        {([['all', 'All'], ...STATUS] as Array<[string, string]>).map(([k, l]) => (
          <button
            key={k}
            onClick={() => setFilter(k as 'all' | Idea['status'])}
            className={
              'px-3 py-1.5 rounded text-sm font-medium border ' +
              (filter === k ? 'bg-ink text-white border-ink' : 'bg-white text-ink-2 border-border hover:bg-surface')
            }
          >
            {l} <span className={'ml-1 ' + (filter === k ? 'text-white/70' : 'text-ink-3')}>{counts[k] ?? 0}</span>
          </button>
        ))}
      </div>

      {err ? (
        <div className="card text-center py-8">
          <div className="text-sm text-ink-2">{err}</div>
          <button onClick={() => { setLoading(true); load() }} className="text-ink-3 hover:text-ink text-sm mt-3 underline">Retry</button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card text-center text-ink-3 py-10">No submissions{filter !== 'all' ? ' in this status' : ' yet'}.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((it) => (
            <Card
              key={it.id}
              it={it}
              onStatus={setStatus}
              onSaveNote={saveNote}
              onSaveAdmin={saveAdminNotes}
              onDelete={remove}
              onView={setLightbox}
              getAccessToken={getAccessToken}
            />
          ))}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="screenshot" className="max-h-[90vh] max-w-full rounded shadow-2xl" />
        </div>
      )}
    </div>
  )
}

interface CardProps {
  it: Idea
  onStatus: (id: string, s: Idea['status']) => void
  onSaveNote: (id: string, note: string) => void
  onSaveAdmin: (id: string, notes: string) => void
  onDelete: (id: string) => void
  onView: (url: string) => void
  getAccessToken: () => Promise<string | undefined>
}

function Card({ it, onStatus, onSaveNote, onSaveAdmin, onDelete, onView, getAccessToken }: CardProps) {
  const [editing, setEditing] = useState(false)
  const [note, setNote] = useState(it.note ?? '')
  const [adminNote, setAdminNote] = useState(it.adminNotes ?? '')
  const adminDirty = adminNote !== (it.adminNotes ?? '')

  return (
    <div className="card flex flex-col sm:flex-row gap-4">
      <Thumb id={it.id} hasShot={!!it.screenshotName} onView={onView} getAccessToken={getAccessToken} />
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={'px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide ' + STATUS_STYLE[it.status]}>
            {STATUS_LABEL[it.status] ?? it.status}
          </span>
          <span className="text-xs text-ink-3">{fmt(it.createdAt)}</span>
        </div>
        {editing ? (
          <div className="mb-2">
            <textarea className="input text-sm" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
            <div className="flex gap-2 mt-1">
              <button className="btn-gold text-xs !px-3 !py-1" onClick={() => { onSaveNote(it.id, note); setEditing(false) }}>Save</button>
              <button className="text-ink-3 hover:text-ink text-xs font-semibold" onClick={() => { setNote(it.note ?? ''); setEditing(false) }}>Cancel</button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-ink whitespace-pre-wrap mb-2">{it.note || <span className="text-ink-3 italic">No note</span>}</p>
        )}
        <div className="text-xs text-ink-3 space-y-0.5">
          <div>
            <span className="font-semibold text-ink-2">From:</span>{' '}
            {it.userName || it.userEmail || 'Anonymous'}
            {it.userEmail && it.userName ? ` (${it.userEmail})` : ''} ·{' '}
            <span className="uppercase">{it.userRole || 'anonymous'}</span>
          </div>
          {it.pageUrl && (
            <div className="truncate">
              <span className="font-semibold text-ink-2">Page:</span>{' '}
              <a href={it.pageUrl} target="_blank" rel="noreferrer" className="text-gold-dark hover:underline">{it.pageUrl}</a>
            </div>
          )}
        </div>

        <div className="mt-3 pt-3 border-t border-border">
          <div className="text-[11px] uppercase tracking-wide font-semibold text-ink-2 mb-1">
            Admin notes <span className="text-ink-3 normal-case font-normal">— internal, for the dev team</span>
          </div>
          <textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} rows={2} className="input text-sm" placeholder="Add a note for the developers…" />
          {adminDirty && (
            <div className="flex gap-2 mt-1">
              <button className="btn-gold text-xs !px-3 !py-1" onClick={() => onSaveAdmin(it.id, adminNote)}>Save notes</button>
              <button className="text-ink-3 hover:text-ink text-xs font-semibold" onClick={() => setAdminNote(it.adminNotes ?? '')}>Cancel</button>
            </div>
          )}
        </div>
      </div>
      <div className="flex sm:flex-col items-end gap-2 shrink-0">
        <select
          value={it.status}
          onChange={(e) => onStatus(it.id, e.target.value as Idea['status'])}
          className="input !w-36 py-1.5 text-sm"
        >
          {STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div className="flex gap-2">
          {!editing && <button onClick={() => setEditing(true)} className="text-ink-3 hover:text-ink text-xs font-semibold">Edit</button>}
          <button onClick={() => onDelete(it.id)} className="text-ink-3 hover:text-red-700 text-xs font-semibold">Delete</button>
        </div>
      </div>
    </div>
  )
}

// Screenshot thumbnail. The endpoint is auth-gated. Auth goes through the
// sealed `evrst_prints_session` cookie (`credentials: 'include'`) — the
// Bearer is best-effort decoration. Crucially, `getAccessToken()` must be
// wrapped in try/catch: users who signed in via the server `/auth/callback`
// flow have a valid sealed cookie but the WorkOS SDK doesn't always know
// about that session, so `getAccessToken()` can THROW. Same pattern apiFetch
// already uses; without it the whole effect short-circuits and the fetch is
// never issued (which was the original "screenshots not showing" bug).
function Thumb({ id, hasShot, onView, getAccessToken }: { id: string; hasShot: boolean; onView: (url: string) => void; getAccessToken: () => Promise<string | undefined> }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!hasShot) return
    let revoked = false
    let obj: string | null = null
    ;(async () => {
      let token: string | undefined
      try { token = await getAccessToken() } catch { /* server-cookie session — SDK has no token; fetch via cookie alone. */ }
      try {
        const r = await fetch(`/api/admin/ideas/${id}/screenshot`, {
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        })
        if (!r.ok) return
        const b = await r.blob()
        if (revoked) return
        obj = URL.createObjectURL(b); setUrl(obj)
      } catch { /* network error — leave loading state, will retry on next mount. */ }
    })()
    return () => { revoked = true; if (obj) URL.revokeObjectURL(obj) }
  }, [id, hasShot, getAccessToken])

  if (!hasShot) {
    return <div className="w-28 h-20 shrink-0 rounded bg-surface border border-border flex items-center justify-center text-ink-3 text-xs">No image</div>
  }
  return (
    <button onClick={() => url && onView(url)} className="w-28 h-20 shrink-0 rounded bg-surface border border-border overflow-hidden hover:ring-2 hover:ring-gold/30">
      {url ? <img src={url} alt="screenshot" className="w-full h-full object-cover" /> : <span className="text-ink-3 text-xs">…</span>}
    </button>
  )
}
